import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  View,
  Platform,
  Pressable,
  NativeModules,
  ScrollView,
} from "react-native";
import * as WebBrowser from "expo-web-browser";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import Text from "@/components/shared/AppText";
import { Ionicons } from "@expo/vector-icons";
import SupportButton from "@/components/support/SupportButton";

import { useAuth } from "../AuthContext";
import { useNavigation } from "@react-navigation/native";
import { formatEventDate } from "../globalUtils";
import { useBuy } from "../BuyContext";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import OnboardingButton from "./OnboardingButton";
import FeaturesList from "./FeaturesList";
import PaywallDurationPicker from "./PaywallDurationPicker";
import { defaultPaywallTier } from "./paywallDefaultTier";
import faqs from "@/components/faqs.json";
import {
  trackPaywallTestView,
  usePaywallTest,
} from "@/components/experiments/paywallTest";
import analytics from "@/analytics";
import AppConfig from "@/AppConfig";
import useRoomGroup from "@/components/rooms/useRoomGroup";
import { isGrouped } from "@/components/rooms/rooms";
import {
  CAMERA_PRODUCT,
  addsCameras,
  isCameraGroup,
} from "@/components/rooms/multicam";
import {
  cameraCheckoutOf,
  cameraPurchaseOf,
  listLabels,
} from "@/components/rooms/cameraCheckout";
import { t, tn, tRich } from "@/localization";
import tw from "twrnc";

const { NotificationManager } = NativeModules;

const PAYWALL_COLORS = {
  ink: "#0F2033",
  muted: "#6B7A8A",
  line: "#E6ECF2",
  coral: "#F05A3C",
  coralSoft: "#FFF2ED",
  gold: "#C9932B",
  goldSoft: "#FBF3DE",
  green: "#1F8A5B",
  greenSoft: "#EAF6F0",
  dimBadge: "#C5CCD4",
};

const tierAccents = {
  coral: [PAYWALL_COLORS.coral, PAYWALL_COLORS.coralSoft],
  gold: [PAYWALL_COLORS.gold, PAYWALL_COLORS.goldSoft],
  green: [PAYWALL_COLORS.green, PAYWALL_COLORS.greenSoft],
};

// Paywall v2 on web (since 2026-09-28), iOS (from 4.3.0) and Android (from
// 4.3.5): 10 years first, "Recommended" 1 year in green, no reference prices,
// and a tap on a tier goes straight to checkout.
const isPaywallV2 = true;

// On web, route params become the URL, so an event object turned into
// "?event=[object Object]"; EventEdit loads the event by slug anyway.
const eventEditParams = (event, slug) =>
  Platform.OS === "web" ? { slug } : { event, slug };
const v2TierOrder = [
  "events_storage_10y",
  "events_storage_1y",
  "events_storage_1m",
];

const storageTiers = [
  {
    productId: "events_storage_1m",
    lengthLabel: t("1 month"),
    tone: "coral",
    badge: null,
  },
  {
    productId: "events_storage_1y",
    lengthLabel: t("1 year"),
    sub: t("A year of memories"),
    tone: "coral",
    badge: t("Recommended"),
  },
  {
    productId: "events_storage_10y",
    lengthLabel: t("10 years"),
    sub: t("A decade of memories"),
    tone: "gold",
    badge: t("Best value"),
  },
];

const enablePaywallDebugLogs = true;
const storageDurationByProductId = {
  events_storage_1m: { amount: 1, unit: "months" },
  events_storage_1y: { amount: 1, unit: "years" },
  events_storage_10y: { amount: 10, unit: "years" },
};

const reviewsByEventType = {
  wedding: {
    text: t(
      "Compared to the $500 we had been quoted from a live streamer who just sets up on camera, this app was far better and so much cheaper. Highly recommend."
    ),
    author: t("Bride review on the App Store"),
    date: "Aug 2, 2022",
  },
  funeral: {
    text: t(
      "This worked flawlessly for livestream a celebration of life for my daughter. We had over 100 people on the day and many have watched on replay. It was easy to set up and I loved the fact that I could test it ahead of the actual event to build confidence that it would work."
    ),
    author: t("{author}, review on the AppStore", { author: "Kipan" }),
    date: "Dec 7, 2023",
  },
  birthday: {
    text: t(
      "Best $ spent on a Bar Mitzvah! It was such a relief to be able to send out a custom link in an invitation and not have to worry about people connecting via an app or having to sign in somewhere."
    ),
    author: t("{author} review on the App Store", { author: "BklynMom2" }),
    date: "Sep 21, 2021",
  },
  sport: {
    text: t("Great affordable easy to use option for streaming an event."),
    author: t("{author} on the App Store", { author: "TheKozo" }),
    date: "Feb 27, 2023",
  },
  school: {
    text: t(
      "Just started using EventLive about a week ago and it has been incredible. It's easy to use, full of features, and very affordable."
    ),
    author: t("{author} on the App Store", { author: "K. Stockham" }),
    date: "Feb 15, 2022",
  },
  other: {
    text: t(
      "So simple and easy to use that I would recommend it for any of your live event needs. Setup and testing makes it easy to know what you need to do on the day."
    ),
    author: t("{author} on the App Store", { author: "b_rowe3" }),
    date: "Oct 17, 2021",
  },
};

// Review dates are written in English ("Aug 2, 2022"); show them in the
// app's language. Anything else is shown as it comes.
const reviewDate = (date) => {
  const parsed = moment(date, "MMM D, YYYY", "en", true);
  return parsed.isValid()
    ? parsed.locale(moment.locale()).format(t("MMM D, YYYY"))
    : date;
};

const ReviewView = ({ eventType }) => {
  const review = reviewsByEventType[eventType] || reviewsByEventType.other;

  return <Review {...review} />;
};

export const Review = ({
  rating = 5,
  title,
  text,
  author,
  date,
  transparent = false,
  fullWidth = false,
  style,
}) => {
  const isMobile = useIsMobileUX();
  return (
    <View
      style={[
        tw.style(
          "rounded-2xl p-5 my-5",
          transparent ? "bg-transparent" : "bg-[rgba(239,239,239,0.5)]"
        ),
        !isMobile && !fullWidth && tw`w-[70%] self-center`,
        style,
      ]}
    >
      <View style={tw`flex-row items-center justify-center mb-0`}>
        {[...Array(rating)].map((_, i) => (
          <Ionicons
            key={i}
            name="star"
            size={22}
            color="gold"
            style={tw`p-1.5`}
          />
        ))}
      </View>
      <Text style={tw`mt-1 mb-2 text-center text-base leading-5`}>
        {'"' + text + '"'}
      </Text>
      <Text style={tw`text-center py-0 italic text-sm`}>
        {author}, {reviewDate(date)}
      </Text>
    </View>
  );
};

const PayWall = ({ event }) => {
  const { account_type, legacyPricing, username, broadcaster } = useAuth();
  // Paywall test (docs/paywall-test.md): "tiers" is today's paywall,
  // "picker" shows one line, "Replay online for 1 year", above the button.
  const paywallTest = usePaywallTest();
  const showPicker = paywallTest.enrolled && paywallTest.variant === "picker";
  const {
    purchase,
    getCurrencyPrice,
    getCurrencyTotal,
    getOriginalCurrencyPrice,
    getDiscountPercent,
    pricingDebugInfo,
    pricingReady,
    pricingCountryCode,
  } = useBuy();
  const isMobileUX = useIsMobileUX();

  const navigation = useNavigation();
  const defaultTierProductId = defaultPaywallTier({
    platform: Platform.OS,
    countryCode: pricingCountryCode,
  });
  const [selectedProductId, setSelectedProductId] =
    useState(defaultTierProductId);
  // The pricing country arrives after the first render; move the default
  // with it until the user picks a tier themselves.
  const userPickedTierRef = useRef(false);
  useEffect(() => {
    if (!userPickedTierRef.current) {
      setSelectedProductId(defaultTierProductId);
    }
  }, [defaultTierProductId]);

  const {
    title,
    slug,
    days = 1,
    days_paid = 0,
    event_type,
    event_timezone,
    event_datetime_utc,
    event_window_end_datetime_utc,
    is_published,
  } = event;

  // Camera groups (components/rooms/cameraCheckout.js): one tap pays for the
  // main camera, if it's still locked, at the tier picked, and every locked
  // camera at the camera price, whatever the tier. Once the main camera is
  // paid, there is nothing to pick: "camerasOnly".
  const camerasMode =
    addsCameras({ account_type }) && isCameraGroup(event) && isGrouped(event);
  const group = useRoomGroup(camerasMode ? event : null);
  const camerasLoading = camerasMode && !group.loaded;
  const groupLoaded = group.loaded;
  const cameraCheckout = useMemo(
    () =>
      camerasMode && groupLoaded
        ? cameraCheckoutOf(group.primary, group.rooms)
        : null,
    [camerasMode, groupLoaded, group.primary, group.rooms]
  );
  const camerasOnly = !!cameraCheckout?.mainPaid;
  const camerasPending = camerasLoading || !!cameraCheckout;

  const eventDate = formatEventDate(event_datetime_utc, event_timezone);
  const daysToPay = is_published ? 0 : days - days_paid;
  // Units of the product picked: the main camera's days (or, cameras only,
  // the cameras'); a camera group's cameras are priced on top.
  const purchaseQuantity = cameraCheckout
    ? cameraCheckout.mainUnits || cameraCheckout.cameraUnits
    : Math.max(daysToPay, 1);
  const normalizedEventDays = Number.isFinite(Number(days))
    ? Math.max(Math.round(Number(days)), 1)
    : 1;
  const fallbackProductId = legacyPricing
    ? "specialevent50"
    : "specialeventnew";

  const priceForTier = useCallback(
    (productId) => {
      if (!pricingReady) return "";
      if (cameraCheckout && !camerasOnly) {
        return getCurrencyTotal([
          { productId, quantity: cameraCheckout.mainUnits },
          { productId: CAMERA_PRODUCT, quantity: cameraCheckout.cameraUnits },
        ]);
      }
      return (
        getCurrencyPrice(productId, purchaseQuantity) ||
        getCurrencyPrice(fallbackProductId, purchaseQuantity)
      );
    },
    [
      getCurrencyPrice,
      getCurrencyTotal,
      fallbackProductId,
      purchaseQuantity,
      pricingReady,
      cameraCheckout,
      camerasOnly,
    ]
  );

  // An extra camera's price, "$35", and what the cameras cost in all.
  const cameraUnitPrice =
    cameraCheckout && pricingReady ? getCurrencyPrice(CAMERA_PRODUCT, 1) : "";
  const cameraPrice =
    camerasOnly && pricingReady
      ? getCurrencyPrice(CAMERA_PRODUCT, cameraCheckout.cameraUnits)
      : "";
  // "$35", or "$35 a day" when the event is longer than a day.
  const cameraUnitLabel = !cameraUnitPrice
    ? ""
    : cameraCheckout.cameraUnits > cameraCheckout.cameraSlugs.length
    ? t("{price} a day", { price: cameraUnitPrice })
    : cameraUnitPrice;

  const originalPriceForTier = useCallback(
    (productId) => {
      if (!pricingReady) return "";
      return (
        (getOriginalCurrencyPrice?.(productId, purchaseQuantity) ?? "") ||
        (getOriginalCurrencyPrice?.(fallbackProductId, purchaseQuantity) ?? "")
      );
    },
    [
      getOriginalCurrencyPrice,
      fallbackProductId,
      purchaseQuantity,
      pricingReady,
    ]
  );

  const discountPctForTier = useCallback(
    (productId) => {
      if (!pricingReady) return 0;
      const direct = getDiscountPercent?.(productId) ?? 0;
      if (direct > 0) return direct;
      return getDiscountPercent?.(fallbackProductId) ?? 0;
    },
    [getDiscountPercent, fallbackProductId, pricingReady]
  );

  const liveInLabel = useMemo(() => {
    if (!event_datetime_utc) return null;
    const zone = event_timezone || moment.tz.guess();
    const when = moment.tz(event_datetime_utc, zone);
    const now = moment.tz(zone);
    if (!when.isValid()) return null;
    if (when.isSameOrBefore(now)) {
      // Started: inside its go-live window, or the window is over.
      return event_window_end_datetime_utc &&
        now.isSameOrAfter(event_window_end_datetime_utc)
        ? t("DATE PASSED – CHANGE IT FOR FREE")
        : t("STARTS TODAY");
    }

    const days = when.diff(now, "days");
    const hours = when.diff(now, "hours");
    const minutes = when.diff(now, "minutes");

    if (days >= 14) {
      const weeks = Math.round(days / 7);
      return tn("LIVE IN {count} WEEK", "LIVE IN {count} WEEKS", weeks);
    }
    if (days >= 1) {
      return tn("LIVE IN {count} DAY", "LIVE IN {count} DAYS", days);
    }
    if (hours >= 1) {
      return tn("LIVE IN {count} HOUR", "LIVE IN {count} HOURS", hours);
    }
    if (minutes >= 1) {
      return t("LIVE IN {minutes} MIN", { minutes });
    }
    return t("GO LIVE NOW");
  }, [event_datetime_utc, event_timezone, event_window_end_datetime_utc]);

  const oneMonthExpirySub = useMemo(() => {
    const baseDate = event_datetime_utc || event_window_end_datetime_utc;
    if (!baseDate) return t("1 month of replay");
    const zone = event_timezone || moment.tz.guess();
    return t("Replay deleted {date}", {
      date: moment(baseDate).tz(zone).add(1, "month").format(t("MMM D")),
    });
  }, [event_datetime_utc, event_window_end_datetime_utc, event_timezone]);

  // "Nov 14, 2027": when a tier's replay ends, counted from the event date.
  const untilDateFor = useCallback(
    (productId) => {
      const baseDate = event_datetime_utc || event_window_end_datetime_utc;
      const length = storageDurationByProductId[productId];
      if (!baseDate || !length) return "";
      const zone = event_timezone || moment.tz.guess();
      return moment(baseDate)
        .tz(zone)
        .add(length.amount, length.unit)
        .format(t("MMM D, YYYY"));
    },
    [event_datetime_utc, event_window_end_datetime_utc, event_timezone]
  );

  // "Until Nov 14, 2027" for the picker's sheet.
  const untilLabelFor = useCallback(
    (productId) => {
      if (productId === "events_storage_1m") return oneMonthExpirySub;
      const date = untilDateFor(productId);
      return date ? t("Until {date}", { date }) : "";
    },
    [oneMonthExpirySub, untilDateFor]
  );

  const tierOptions = useMemo(() => {
    const tiers = isPaywallV2
      ? v2TierOrder.map((productId) =>
          storageTiers.find((tier) => tier.productId === productId)
        )
      : storageTiers;

    return tiers.map((tier) => {
      const is1m = tier.productId === "events_storage_1m";
      const hideReferencePrice = is1m || isPaywallV2;
      const recommended = isPaywallV2 && tier.productId === "events_storage_1y";
      return {
        ...tier,
        ...(recommended && { badge: t("Recommended"), tone: "green" }),
        sub: is1m ? oneMonthExpirySub : tier.sub,
        untilLabel: untilLabelFor(tier.productId),
        localizedPrice: priceForTier(tier.productId),
        originalLocalizedPrice: hideReferencePrice
          ? ""
          : originalPriceForTier(tier.productId),
        discountPct: hideReferencePrice
          ? 0
          : discountPctForTier(tier.productId),
      };
    });
  }, [
    priceForTier,
    originalPriceForTier,
    discountPctForTier,
    oneMonthExpirySub,
    untilLabelFor,
  ]);

  const selectedTier = tierOptions.find(
    (tier) => tier.productId === selectedProductId
  );
  const ctaColor =
    !camerasOnly && selectedTier?.tone === "gold"
      ? PAYWALL_COLORS.gold
      : PAYWALL_COLORS.coral;

  // Cameras only: "Nov 14, 2027", when the main camera's replay ends: its
  // expiry, or its tier's date when it has none. "" when there's no date to
  // give (a 1-month tier says it its own way, "Replay deleted Dec 14").
  const cameraUntilDate = useMemo(() => {
    if (!camerasOnly) return "";
    if (!cameraCheckout.expiry) {
      return cameraCheckout.tier === "events_storage_1m"
        ? ""
        : untilDateFor(cameraCheckout.tier);
    }
    const zone = event_timezone || moment.tz.guess();
    return moment(cameraCheckout.expiry).tz(zone).format(t("MMM D, YYYY"));
  }, [
    camerasOnly,
    cameraCheckout?.expiry,
    cameraCheckout?.tier,
    event_timezone,
    untilDateFor,
  ]);
  // Cameras only: "Until Nov 14, 2027", the main camera's replay.
  const cameraUntilLabel = !camerasOnly
    ? ""
    : cameraUntilDate
    ? t("Until {date}", { date: cameraUntilDate })
    : untilLabelFor(cameraCheckout.tier);
  const cameraFeatures = camerasOnly
    ? [
        {
          icon: "videocam-outline",
          description: t("More angles on the same link"),
          benefits: t(
            "Guests switch between the cameras in the player. Nothing new to send."
          ),
        },
        {
          icon: "chatbubbles-outline",
          description: t("One guestbook for every angle"),
          benefits: t("Messages stay together, whichever camera guests watch."),
        },
        {
          icon: "download-outline",
          description: t("Replay & download, like your main camera"),
          benefits: t("{replay}. Each camera keeps its own recording.", {
            replay: cameraUntilLabel,
          }),
        },
        {
          icon: "phone-portrait-outline",
          description: t("Stream from any phone"),
          benefits: t(
            "Send each camera's link or QR code to whoever holds it."
          ),
        },
      ]
    : null;

  // "One-time · 3 cameras" or "One-time · 2 days"; "" when it's one event
  // for one day, plain "One-time".
  const oneTimeCount =
    cameraCheckout && !camerasOnly
      ? t("One-time · {count} cameras", { count: cameraCheckout.cameras })
      : !cameraCheckout && purchaseQuantity > 1
      ? t("One-time · {count} days", { count: purchaseQuantity })
      : "";
  const oneTimeLabel = oneTimeCount || t("One-time");
  const unlockLabel = !cameraCheckout
    ? t("Unlock event")
    : camerasOnly && cameraCheckout.cameras === 1
    ? t("Unlock {name}", { name: cameraCheckout.labels[0] })
    : t("Unlock {count} cameras", { count: cameraCheckout.cameras });
  const ctaPrice = camerasOnly ? cameraPrice : selectedTier?.localizedPrice;
  const pricesLoaded = pricingReady && paywallTest.ready && !camerasLoading;
  // Cameras are never sold at another product's price: without theirs (an
  // evt.live or store that doesn't have it yet), there's no checkout.
  const camerasPriced = !cameraCheckout || !!cameraUnitPrice;
  const checkoutReady = pricesLoaded && camerasPriced;

  function setupNotificationOnBackground() {
    try {
      if (Platform.OS === "ios" && NotificationManager) {
        NotificationManager.setupNotificationOnBackground(title);
        console.log("Native notification setup initiated for event:", title);
      }
    } catch (error) {
      console.error("Failed to setup native notification:", error);
    }
  }

  useEffect(() => {
    if (account_type === "business") return;
    // A paid main camera can still have cameras to unlock here.
    if (camerasPending) return;
    if (is_published && !event.is_test) {
      navigation.navigate("EventEdit", eventEditParams(event, event.slug));
    }
  }, [account_type, is_published, event, navigation, camerasPending]);

  useEffect(() => {
    if (account_type === "business") {
      navigation.replace("Plans", {
        source: "event_paywall",
        event,
        slug,
      });
    }
  }, [account_type, event, slug, navigation]);

  useEffect(() => {
    if (account_type === "business") return;
    // Only request provisional notifications on iOS
    if (Platform.OS === "ios") {
      setupNotificationOnBackground();
    }
  }, [account_type, title]);

  useEffect(() => {
    if (!enablePaywallDebugLogs) return;

    console.log("[PayWall] Pricing snapshot", {
      slug,
      purchaseQuantity,
      selectedProductId,
      fallbackProductId,
      pricingDebugInfo,
      tierOptions: tierOptions.map(
        ({ productId, lengthLabel, localizedPrice }) => ({
          productId,
          lengthLabel,
          localizedPrice,
        })
      ),
    });
  }, [
    fallbackProductId,
    pricingDebugInfo,
    purchaseQuantity,
    selectedProductId,
    slug,
    tierOptions,
  ]);

  const loggedTestViewRef = useRef(false);
  useEffect(() => {
    if (loggedTestViewRef.current || !paywallTest.ready || !pricingReady)
      return;
    if (account_type === "business" || (is_published && !event.is_test)) return;
    // Cameras for a paid event aren't part of the paywall test.
    if (camerasLoading || camerasOnly) return;
    loggedTestViewRef.current = true;
    trackPaywallTestView(paywallTest, username || broadcaster, {
      slug,
      event_type,
      days: normalizedEventDays,
      default_product: selectedProductId,
    });
  }, [
    paywallTest,
    pricingReady,
    account_type,
    is_published,
    event,
    username,
    broadcaster,
    slug,
    event_type,
    normalizedEventDays,
    selectedProductId,
    camerasLoading,
    camerasOnly,
  ]);

  const buy = async (pickedProductId = selectedProductId) => {
    if (!checkoutReady) {
      return;
    }
    // Cameras for a paid event: the camera product, kept as long as the main
    // camera.
    const productId = camerasOnly ? CAMERA_PRODUCT : pickedProductId;

    // Both paywalls log the tap the same way, whichever checkout follows
    // (App Store, Google Play or Stripe).
    analytics.track("Paywall Unlock Tapped", {
      product: productId,
      price: camerasOnly
        ? cameraPrice
        : tierOptions.find((tier) => tier.productId === productId)
            ?.localizedPrice,
      quantity: purchaseQuantity,
      slug,
      paywall: camerasOnly ? "cameras" : showPicker ? "picker" : "tiers",
      ...(cameraCheckout && {
        cameras: cameraCheckout.cameras,
        camera_units: cameraCheckout.cameraUnits,
      }),
      ...(paywallTest.enrolled &&
        !camerasOnly && {
          test_id: paywallTest.testId,
          variant: paywallTest.variant,
        }),
    });

    // Cancel notification setup when purchasing
    if (Platform.OS === "ios" && NotificationManager) {
      try {
        NotificationManager.cancelNotificationSetup();
      } catch (error) {
        console.error("Failed to cancel notification setup:", error);
      }
    }

    // Cameras are kept as long as the main camera: its expiry, or its tier's
    // from the event date when it has none.
    const selectedStorageDuration =
      storageDurationByProductId[camerasOnly ? cameraCheckout.tier : productId];
    const baseEventDateTimeUtc =
      event_datetime_utc || event_window_end_datetime_utc;
    const datetime_expiry =
      (camerasOnly && cameraCheckout.expiry) ||
      (selectedStorageDuration && baseEventDateTimeUtc
        ? moment(baseEventDateTimeUtc)
            .add(selectedStorageDuration.amount, selectedStorageDuration.unit)
            .utc()
            .format()
        : undefined);
    const target = cameraCheckout
      ? cameraPurchaseOf(group.primary, cameraCheckout, productId)
      : { productId, slug, quantity: purchaseQuantity };
    if (target.followUp) {
      const cameraLabels = listLabels(
        cameraCheckout.labels.slice(cameraCheckout.mainUnits ? 1 : 0)
      );
      target.followUp = {
        ...target.followUp,
        datetime_expiry,
        notBoughtTitle: t("Extra cameras still locked"),
        notBoughtMessage: tn(
          "Your event is unlocked. {cameras} is still locked: you can unlock it from the event at any time.",
          "Your event is unlocked. {cameras} are still locked: you can unlock them from the event at any time.",
          cameraCheckout.cameraSlugs.length,
          { cameras: cameraLabels }
        ),
      };
    }

    if (enablePaywallDebugLogs) {
      console.log("[PayWall] Starting purchase", {
        ...target,
        datetime_expiry,
      });
    }

    await purchase({
      ...target,
      title,
      datetime_expiry,
    });
  };

  const openPolicyLink = async (url) => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank");
      return;
    }
    await WebBrowser.openBrowserAsync(url);
  };

  const openTerms = () => openPolicyLink(AppConfig.TERMS_URL);
  const openPrivacy = () => openPolicyLink(AppConfig.PRIVACY_URL);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    if (window.Intercom) {
      window.Intercom("update", { hide_default_launcher: true });
    }
    return () => {
      if (window.Intercom) {
        window.Intercom("update", { hide_default_launcher: false });
      }
    };
  }, []);

  const close = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.navigate("EventEdit", eventEditParams(event, slug));
  };

  if (account_type === "business") {
    return (
      <View style={tw`flex-1 items-center justify-center`}>
        <Text style={tw`text-sm text-gray-500`}>{t("Opening plans...")}</Text>
      </View>
    );
  }

  return (
    <View style={tw`flex-1 w-full max-w-[640px] self-center`}>
      <ScrollView
        style={tw`flex-1`}
        contentContainerStyle={tw`w-full px-6 pt-1 pb-[260px]`}
        showsVerticalScrollIndicator={false}
        nativeID="paywall-scroll"
      >
        <View style={tw`flex-row items-center justify-between mt-3 mb-2`}>
          <Pressable onPress={close} hitSlop={8} style={tw`p-1 -ml-1`}>
            <Ionicons name="close-outline" size={28} color="#6B7A8A" />
          </Pressable>
          <View style={tw`flex-row items-center shrink ml-2`}>
            {!!liveInLabel && (
              <View
                style={[
                  tw`flex-row items-center rounded-full px-2.5 py-1 shrink`,
                  { backgroundColor: PAYWALL_COLORS.coralSoft },
                ]}
              >
                <View
                  style={[
                    tw`rounded-full mr-1.5`,
                    {
                      width: 6,
                      height: 6,
                      backgroundColor: PAYWALL_COLORS.coral,
                    },
                  ]}
                />
                <Text
                  style={[
                    tw`text-[10.5px] font-bold tracking-wider shrink`,
                    { color: "#D94526" },
                  ]}
                >
                  {liveInLabel}
                </Text>
              </View>
            )}
            {(Platform.OS !== "web" || isMobileUX) && (
              <SupportButton color="#6B7A8A" style={tw`p-1 -mr-1 ml-3`} />
            )}
          </View>
        </View>

        {camerasOnly ? (
          <Text
            style={[
              tw`text-[26px] leading-[30px] font-bold mt-2 mb-2`,
              { color: PAYWALL_COLORS.ink, letterSpacing: -0.5 },
            ]}
          >
            {t("Unlock {name}", { name: listLabels(cameraCheckout.labels) })}
          </Text>
        ) : (
          <Text
            style={[
              tw`text-[26px] leading-[30px] font-bold mt-2 mb-2`,
              { color: PAYWALL_COLORS.ink, letterSpacing: -0.5 },
            ]}
          >
            {tRich("Stream your event to <em>everyone</em> who matters.", {
              em: (words) => (
                <Text
                  style={{
                    color: PAYWALL_COLORS.coral,
                    fontSize: 26,
                    lineHeight: 30,
                    fontStyle: "italic",
                    fontWeight: "700",
                  }}
                >
                  {words}
                </Text>
              ),
            })}
          </Text>
        )}

        <FeaturesList
          streamDate={eventDate}
          liveDays={normalizedEventDays}
          replayFor={showPicker ? selectedTier?.lengthLabel : null}
          cameras={cameraCheckout?.cameras || 1}
          features={cameraFeatures}
        />

        <View style={tw`-mt-3`}>
          <ReviewView eventType={event_type} />
        </View>

        <View style={tw`pb-4`}>
          <Text style={tw`font-bold text-lg text-[#253447] mb-1`}>
            {t("FAQs")}
          </Text>
          {faqs.map((faq, index) => (
            <FAQItem key={index} faq={faq} />
          ))}
        </View>
      </ScrollView>

      <View style={tw`border-t border-gray-200 bg-white px-4 pt-3 pb-2`}>
        {Platform.OS === "web" && (
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: -18,
              height: 18,
              backgroundImage:
                "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
            }}
          />
        )}
        <View style={tw`w-full`}>
          {/* social proof — right above tiers */}
          <View style={tw`flex-row items-center justify-center mb-4`}>
            <View style={tw`flex-row mr-2`}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Ionicons
                  key={i}
                  name="star"
                  size={12}
                  color={PAYWALL_COLORS.gold}
                  style={tw`mr-0.5`}
                />
              ))}
            </View>
            <Text style={[tw`text-[11.5px]`, { color: PAYWALL_COLORS.muted }]}>
              {tRich("<b>12,400+</b> events streamed this year", {
                b: (words) => (
                  <Text style={[tw`font-bold`, { color: PAYWALL_COLORS.ink }]}>
                    {words}
                  </Text>
                ),
              })}
            </Text>
          </View>

          {cameraCheckout && !camerasOnly && (
            <Text
              style={[
                tw`text-[12px] text-center -mt-2 mb-3`,
                { color: PAYWALL_COLORS.muted },
              ]}
            >
              {cameraUnitLabel
                ? tn(
                    "Includes an extra camera at {price}, kept as long as your event.",
                    "Includes {count} extra cameras at {price} each, kept as long as your event.",
                    cameraCheckout.cameraSlugs.length,
                    { price: cameraUnitLabel }
                  )
                : tn(
                    "Includes an extra camera, kept as long as your event.",
                    "Includes {count} extra cameras, kept as long as your event.",
                    cameraCheckout.cameraSlugs.length
                  )}
            </Text>
          )}

          {camerasOnly && (
            <CamerasRow
              heading={
                cameraUnitLabel
                  ? t("Extra cameras, {price} each", { price: cameraUnitLabel })
                  : t("Extra cameras")
              }
              title={
                cameraCheckout.cameras === 1
                  ? cameraCheckout.labels[0]
                  : t("{count} cameras", { count: cameraCheckout.cameras })
              }
              sub={
                cameraUntilDate
                  ? t("Replay until {date}", { date: cameraUntilDate })
                  : cameraUntilLabel
              }
              price={cameraPrice}
            />
          )}

          {paywallTest.ready && !camerasOnly && showPicker && (
            <PaywallDurationPicker
              tiers={tierOptions}
              selectedProductId={selectedProductId}
              testId={paywallTest.testId}
              onSelect={(productId) => {
                userPickedTierRef.current = true;
                setSelectedProductId(productId);
                // Like a tier card: picking a length goes straight to checkout.
                if (isPaywallV2) buy(productId);
              }}
            />
          )}

          {paywallTest.ready && !camerasOnly && !showPicker && (
            <>
              {/* header row */}
              <View
                style={tw`flex-row items-center justify-between px-0.5 mb-1`}
              >
                <Text
                  style={[
                    tw`flex-1 mr-2 text-[13px] font-bold`,
                    { color: PAYWALL_COLORS.ink },
                  ]}
                >
                  {t("How long to keep the replay?")}
                </Text>
                <View
                  style={[
                    tw`px-1.5 py-0.5 rounded`,
                    { backgroundColor: "rgba(31,138,91,0.12)" },
                  ]}
                >
                  <Text
                    style={[
                      tw`text-[10px] font-extrabold tracking-wider uppercase`,
                      { color: PAYWALL_COLORS.green },
                    ]}
                  >
                    {oneTimeCount || t("One-time payment")}
                  </Text>
                </View>
              </View>

              {/* tiers — vertical stack of horizontal rows */}
              <View>
                {tierOptions.map((tier, index) => {
                  const selected = selectedProductId === tier.productId;
                  const [accent, accentSoft] =
                    tierAccents[tier.tone] || tierAccents.coral;
                  const dimBadge =
                    tier.productId === "events_storage_1y" &&
                    selectedProductId === "events_storage_10y";
                  const badgeColor = dimBadge
                    ? PAYWALL_COLORS.dimBadge
                    : accent;

                  return (
                    <Pressable
                      key={tier.productId}
                      onPress={() => {
                        userPickedTierRef.current = true;
                        setSelectedProductId(tier.productId);
                        if (isPaywallV2) buy(tier.productId);
                      }}
                      accessibilityRole={isPaywallV2 ? "button" : undefined}
                      style={[
                        tw`relative flex-row items-center rounded-2xl px-3 py-2`,
                        {
                          borderWidth: 2,
                          borderColor: selected ? accent : PAYWALL_COLORS.line,
                          backgroundColor: selected ? accentSoft : "#FFFFFF",
                          marginTop: tier.badge ? 14 : index === 0 ? 2 : 8,
                        },
                      ]}
                    >
                      {!!tier.badge && (
                        <View
                          style={[
                            tw`absolute px-2 py-0.5 rounded-full`,
                            {
                              top: -9,
                              left: 12,
                              backgroundColor: badgeColor,
                              zIndex: 2,
                            },
                          ]}
                        >
                          <Text
                            style={tw`text-[9.5px] font-bold text-white tracking-wider uppercase`}
                          >
                            {tier.badge}
                          </Text>
                        </View>
                      )}

                      {/* radio */}
                      <View
                        style={[
                          tw`mr-2.5 rounded-full bg-white`,
                          {
                            width: 20,
                            height: 20,
                            borderWidth: selected ? 6 : 2,
                            borderColor: selected
                              ? accent
                              : PAYWALL_COLORS.line,
                          },
                        ]}
                      />

                      {/* title + sub */}
                      <View style={tw`flex-1 min-w-0 mr-2`}>
                        <Text
                          style={[
                            tw`text-[15px] font-bold`,
                            { color: PAYWALL_COLORS.ink },
                          ]}
                          numberOfLines={1}
                        >
                          {t("Replay for {length}", {
                            length: tier.lengthLabel,
                          })}
                        </Text>
                        {!!tier.sub && (
                          <Text
                            style={[
                              tw`text-[11.5px] mt-0.5`,
                              { color: PAYWALL_COLORS.muted },
                            ]}
                            numberOfLines={1}
                          >
                            {tier.sub}
                          </Text>
                        )}
                      </View>

                      {/* price + savings */}
                      <View style={tw`items-end shrink-0`}>
                        <View style={tw`flex-row items-baseline`}>
                          {!!tier.originalLocalizedPrice && (
                            <Text
                              style={[
                                tw`text-[13px] font-semibold mr-1.5`,
                                {
                                  color: "#A8B2BD",
                                  textDecorationLine: "line-through",
                                },
                              ]}
                              numberOfLines={1}
                            >
                              {tier.originalLocalizedPrice}
                            </Text>
                          )}
                          <Text
                            style={[
                              tw`text-[22px] font-extrabold`,
                              { color: PAYWALL_COLORS.ink },
                            ]}
                            numberOfLines={1}
                            adjustsFontSizeToFit
                            minimumFontScale={0.6}
                          >
                            {tier.localizedPrice || "--"}
                          </Text>
                        </View>
                        {tier.discountPct > 0 && (
                          <View
                            style={[
                              tw`mt-0.5 px-1.5 py-0.5 rounded`,
                              { backgroundColor: "rgba(31,138,91,0.12)" },
                            ]}
                          >
                            <Text
                              style={[
                                tw`text-[10px] font-extrabold tracking-wider`,
                                { color: PAYWALL_COLORS.green },
                              ]}
                            >
                              {t("SAVE {percent}%", {
                                percent: tier.discountPct,
                              })}
                            </Text>
                          </View>
                        )}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            </>
          )}

          <OnboardingButton
            onPress={() => buy()}
            disabled={!checkoutReady}
            style={[
              tw`${showPicker && !camerasOnly ? "mt-2" : "mt-4"} mb-1`,
              { backgroundColor: ctaColor },
            ]}
            text={
              checkoutReady
                ? `${unlockLabel}${ctaPrice ? ` – ${ctaPrice}` : ""}`
                : pricesLoaded
                ? t("Prices unavailable")
                : t("Loading Prices")
            }
            loading={!pricesLoaded}
            icon={Platform.OS === "web" ? `card-outline` : null}
          />

          <View
            style={tw`mt-1 flex-row justify-between items-center gap-3 px-1`}
          >
            <Pressable onPress={openTerms}>
              <Text style={tw`text-xs text-[#3F5B7A] underline`}>
                {t("Terms")}
              </Text>
            </Pressable>
            <Text
              style={tw`shrink text-xs text-green-700 text-center font-bold`}
            >
              {showPicker && !camerasOnly
                ? t("{payment} · Money-back guarantee", {
                    payment: oneTimeLabel,
                  })
                : t("100% money-back guarantee")}
            </Text>
            <Pressable onPress={openPrivacy}>
              <Text style={tw`text-xs text-[#3F5B7A] underline`}>
                {t("Privacy")}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
};

// Cameras for a paid event: what the one checkout buys, at a fixed length.
const CamerasRow = ({ heading, title, sub, price }) => (
  <View>
    <Text
      style={[
        tw`text-[13px] font-bold px-0.5 mb-1`,
        { color: PAYWALL_COLORS.ink },
      ]}
    >
      {heading}
    </Text>
    <View
      style={[
        tw`flex-row items-center rounded-2xl px-3 py-2 mt-1`,
        {
          borderWidth: 2,
          borderColor: PAYWALL_COLORS.green,
          backgroundColor: PAYWALL_COLORS.greenSoft,
        },
      ]}
    >
      <Ionicons
        name="videocam"
        size={20}
        color={PAYWALL_COLORS.green}
        style={tw`mr-2.5`}
      />
      <View style={tw`flex-1 min-w-0 mr-2`}>
        <Text
          style={[tw`text-[15px] font-bold`, { color: PAYWALL_COLORS.ink }]}
          numberOfLines={1}
        >
          {title}
        </Text>
        {!!sub && (
          <Text
            style={[tw`text-[11.5px] mt-0.5`, { color: PAYWALL_COLORS.muted }]}
            numberOfLines={1}
          >
            {sub}
          </Text>
        )}
      </View>
      <Text
        style={[tw`text-[22px] font-extrabold`, { color: PAYWALL_COLORS.ink }]}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.6}
      >
        {price || "--"}
      </Text>
    </View>
  </View>
);

const FAQItem = ({ faq }) => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
    analytics.track("FAQ View", { title: faq.title });
  };

  return (
    <View key={faq.title}>
      <Pressable onPress={toggleVisibility}>
        <View style={tw`flex-row items-center py-2.5`}>
          <Ionicons
            name={isVisible ? "chevron-up" : "chevron-down"}
            size={16}
            color="#7A8699"
            style={tw`mr-2`}
          />
          <Text style={tw`font-bold text-left flex-1`}>{t(faq.title)}</Text>
        </View>
      </Pressable>
      {isVisible && <Text style={tw`mb-5 pl-6`}>{t(faq.content)}</Text>}
    </View>
  );
};

export default PayWall;
PayWall.useOwnScrollLayout = true;
PayWall.hideSubScreenHeader = true;
